import {
  LESSON_EDITABLE_VERSION_EDITOR_ELIGIBLE_ROLES,
  LESSON_EDITION_STATUSES,
} from '@didaskill/domain/lessons/constants';
import { USER_ROLES } from '@didaskill/domain/users/constants';
import type { UserAuth } from '@didaskill/domain/users/types';

export {
  computeLessonEditionFlags,
  type LessonEditionFlags,
} from '@didaskill/domain/lessons/utils/edition-flags';

const LESSON_MANAGER_ROLES: USER_ROLES[] = [USER_ROLES.ADMIN, USER_ROLES.CONCEPTION];

type LessonPermissionUser = Pick<UserAuth, 'id' | 'roles'>;

const UNRESTRICTED_LESSON_READ_ROLES: USER_ROLES[] = [
  USER_ROLES.ADMIN,
  USER_ROLES.CONCEPTION,
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.TEACHER,
];

/** Roles whose lesson READ access is instance-scoped, matching domain-ability. */
const SCOPED_LESSON_READ_ROLES: USER_ROLES[] = [USER_ROLES.EDITOR, USER_ROLES.EXTERNAL_EDITOR];

/**
 * Returns whether the user roles include an eligible lesson editable version editor role.
 *
 * @param roles - User roles.
 * @returns True when the user may be assigned as an editable version editor.
 */
export function hasLessonEditableVersionEditorRole(roles: USER_ROLES[]): boolean {
  return roles.some((role) => LESSON_EDITABLE_VERSION_EDITOR_ELIGIBLE_ROLES.includes(role));
}

/**
 * Returns whether the user has unrestricted lesson management rights.
 *
 * @param user - Authenticated user.
 * @returns True when the user can manage any lesson.
 */
export function canUserManageLessons(user: LessonPermissionUser): boolean {
  return user.roles.some((role) => LESSON_MANAGER_ROLES.includes(role));
}

/**
 * Returns whether the user can update an editable lesson version content.
 *
 * @param user - Authenticated user.
 * @param editableVersion - Editable version metadata.
 * @param editableVersion.editors - Editors assigned on the editable version.
 * @param editableVersion.status - Current edition status.
 * @param lessonResponsibles - Lesson-level responsible user ids.
 * @returns True when the user may edit the version.
 */
export function canUserUpdateEditableVersion(
  user: LessonPermissionUser,
  editableVersion: {
    editors: { id: string }[];
    status: LESSON_EDITION_STATUSES;
  },
  lessonResponsibles: string[] = [],
): boolean {
  if (canUserManageLessons(user)) {
    return true;
  }

  if (editableVersion.status !== LESSON_EDITION_STATUSES.EDITING) {
    return false;
  }

  if (user.roles.includes(USER_ROLES.EDITOR)) {
    return editableVersion.editors.some((editor) => editor.id === user.id);
  }

  if (user.roles.includes(USER_ROLES.EXTERNAL_EDITOR)) {
    return lessonResponsibles.includes(user.id);
  }

  return false;
}

/**
 * Returns whether the user may bypass editable sequence locks.
 *
 * @param user - Authenticated user.
 * @param lessonResponsibles - Lesson-level responsible user ids.
 * @returns True when sequence lock checks should be skipped.
 */
export function canUserBypassEditableSequenceLock(
  user: LessonPermissionUser,
  lessonResponsibles: string[] = [],
): boolean {
  if (canUserManageLessons(user)) {
    return true;
  }

  if (user.roles.includes(USER_ROLES.ADMINISTRATIVE)) {
    return true;
  }

  if (user.roles.includes(USER_ROLES.EXTERNAL_EDITOR)) {
    return lessonResponsibles.includes(user.id);
  }

  return false;
}

/**
 * Returns whether the user should acquire a lock when editing a sequence.
 *
 * @param user - Authenticated user.
 * @param lessonResponsibles - Lesson-level responsible user ids.
 * @returns True when saving a sequence should lock it for the user.
 */
export function shouldAcquireEditableSequenceLock(
  user: LessonPermissionUser,
  lessonResponsibles: string[] = [],
): boolean {
  return !canUserBypassEditableSequenceLock(user, lessonResponsibles);
}

/**
 * Returns whether the user can read any lesson without instance-level checks.
 *
 * @param roles - Authenticated user roles.
 * @returns True for roles with unrestricted lesson read access.
 */
export function hasUnrestrictedLessonReadAccess(roles: USER_ROLES[]): boolean {
  return roles.some((role) => UNRESTRICTED_LESSON_READ_ROLES.includes(role));
}

/**
 * Returns whether the user can read the given lesson instance.
 *
 * @param user - Authenticated user.
 * @param lesson - Lesson metadata used for instance checks.
 * @param lesson.responsibles - Lesson responsible user ids.
 * @param lesson.editableVersions - Editable versions with editor user ids.
 * @returns True when the user may read the lesson.
 */
export function canUserReadLesson(
  user: LessonPermissionUser,
  lesson: {
    responsibles?: string[];
    editableVersions?: { editors: string[] }[];
  },
): boolean {
  if (hasUnrestrictedLessonReadAccess(user.roles)) {
    return true;
  }

  const hasUnscopedLessonReadRole = user.roles.some(
    (role) => !SCOPED_LESSON_READ_ROLES.includes(role),
  );

  if (hasUnscopedLessonReadRole) {
    return true;
  }

  if (
    user.roles.includes(USER_ROLES.EXTERNAL_EDITOR) &&
    (lesson.responsibles ?? []).includes(user.id)
  ) {
    return true;
  }

  if (!user.roles.includes(USER_ROLES.EDITOR)) {
    return false;
  }

  return Boolean(lesson.editableVersions?.some((version) => version.editors.includes(user.id)));
}

/**
 * Returns whether an editable version is visible for a scoped lesson access.
 *
 * @param params - Filter parameters.
 * @param params.editors - Editable version editors.
 * @param params.editorId - Editor id used for internal editor scoping.
 * @param params.responsibleId - Responsible id used for external editor scoping.
 * @param params.lessonResponsibles - Lesson-level responsible user ids.
 * @returns True when the editable version should be included.
 */
export function canAccessEditableVersion({
  editors,
  editorId,
  responsibleId,
  lessonResponsibles,
}: {
  editors: string[] | { id: string }[];
  editorId?: string;
  responsibleId?: string;
  lessonResponsibles: string[];
}): boolean {
  if (!editorId && !responsibleId) {
    return true;
  }

  if (responsibleId) {
    return lessonResponsibles.includes(responsibleId);
  }

  const editorIds = editors.map((editor) => (typeof editor === 'string' ? editor : editor.id));

  return editorIds.includes(editorId);
}
